/**
 * Survey system types — shared between frontend and backend.
 *
 * The survey system uses a "snapshot-in-response" architecture:
 * - Form definitions live as TypeScript constants in code (not in the DB)
 * - When a user submits, the question text + answer are saved together
 * - This means each response is self-describing — you never need to look up
 *   the original form to understand what a user answered
 */

// ─── Form Types ─────────────────────────────────────────────
// These identify WHICH survey was submitted. Using `as const` objects
// so we get both runtime values and TypeScript type safety.

export const SURVEY_FORM_TYPES = {
  ACCOUNT_ONBOARDING: 'account_onboarding',
  USER_ONBOARDING: 'user_onboarding',
  OFFBOARDING: 'offboarding',
  NPS: 'nps',
} as const;
export type SurveyFormType = (typeof SURVEY_FORM_TYPES)[keyof typeof SURVEY_FORM_TYPES];

// ─── Question Types ─────────────────────────────────────────
// What kind of input the question uses (single choice, multi choice, etc.)

export const SURVEY_QUESTION_TYPES = {
  SINGLE_CHOICE: 'single_choice',
  MULTI_CHOICE: 'multi_choice',
  TEXT: 'text',
  RATING: 'rating',
} as const;
export type SurveyQuestionType = (typeof SURVEY_QUESTION_TYPES)[keyof typeof SURVEY_QUESTION_TYPES];

// ─── Form Definition Interfaces ─────────────────────────────
// Used in the frontend to define what questions to show

export interface SurveyQuestionChoice {
  value: string;
  label: string;
  description?: string;
}

export interface SurveyQuestion {
  key: string;
  type: SurveyQuestionType;
  text: string;
  description?: string;
  choices?: SurveyQuestionChoice[];
  required?: boolean;
}

export interface SurveyFormDefinition {
  formId: string;
  formVersion: number;
  formType: SurveyFormType;
  questions: SurveyQuestion[];
}

// ─── Answer Snapshot ────────────────────────────────────────
// What gets saved to the database per question when the user submits

export interface SurveyQuestionAnswer {
  key: string; // question identifier, e.g. 'use_case'
  text: string; // the question text at time of submission
  answer: string; // the selected value, e.g. 'menu-boards'
  answerLabel: string; // human-readable label, e.g. 'Menu Boards'
}
